<template>
  <section class="menu-workspace">
    <!-- 顶栏操作和过滤框 -->
    <table-toolbar
      :search.sync="search"
      search-placeholder="输入渠道 ID 搜索..."
      :show-refresh="true"
      :show-export="true"
      :show-column-settings="true"
      :columns="columnOptions"
      :visible-columns.sync="visibleColumns"
      :default-columns="[]"
      :fixed-columns="['# ID', '游戏 ID', '渠道 ID', '卸载菜单', '操作']"
      @refresh="handleReset"
      @export="handleExport"
    >
      <template slot="left">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd" class="btn-action">新增菜单</el-button>

        <!-- 生效重载 -->
        <template v-if="needReload">
          <el-button type="warning" size="small" icon="el-icon-warning-outline" @click="handleReload" class="btn-reload">重载卸载菜单</el-button>
          <span class="reload-tip">👈 发现数据已修改，点击重载才能使服务端生效！</span>
        </template>
      </template>
    </table-toolbar>

    <!-- 表格主体 -->
    <table-page
      :loading="tableLoading"
      :empty="tables.length === 0"
      :current-page.sync="currentPage"
      :page-size.sync="pagesize"
      :total="totalCount"
      :page-sizes="[10, 20, 50, 100, 200]"
      :show-top-pagination="!!totalCount"
      :show-bottom-pagination="!!totalCount"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      @retry="fetchData"
    >
      <el-table
        :data="tables"
        stripe
        style="width: 100%"
        class="premium-table"
      >
        <el-table-column key="col-id" prop="id" label="# ID" width="100">
          <template slot-scope="scope">
            <span class="cell-id">{{ getRowId(scope.row) }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-appId" prop="appId" label="游戏 ID" width="120">
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.appId }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-channelId" prop="channelId" label="渠道 ID" min-width="150">
          <template slot-scope="scope">
            <el-link type="primary" class="cell-name-link" @click="handleEdit(scope.row, scope.$index)">
              {{ scope.row.channelId }}
            </el-link>
          </template>
        </el-table-column>

        <el-table-column key="col-menuArgs" prop="menuArgs" label="卸载菜单" min-width="120">
          <template slot-scope="scope">
            <el-link type="primary" class="menu-detail-link" @click="showMenuDetail(scope.row)">
              <i class="el-icon-view"></i> 查看配置
            </el-link>
          </template>
        </el-table-column>

        <el-table-column key="col-createdAt" v-if="visibleColumns.includes('createdAt')" prop="createdAt" label="创建时间" min-width="180">
          <template slot-scope="scope">
            <span class="time-text">{{ formatTime(scope.row.createdAt) }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-updatedAt" v-if="visibleColumns.includes('updatedAt')" prop="updatedAt" label="修改时间" min-width="180">
          <template slot-scope="scope">
            <span class="time-text">{{ formatTime(scope.row.updatedAt) }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-actions" label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-dropdown @command="handleActionCommand($event, scope.row, scope.$index)" trigger="hover">
              <el-button size="mini" class="action-btn" circle icon="el-icon-more-outline"></el-button>
              <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
                <el-dropdown-item command="edit">
                  <i class="el-icon-edit"></i> 编辑配置
                </el-dropdown-item>
                <el-dropdown-item command="delete" style="color: #ef4444;">
                  <i class="el-icon-delete" style="color: #ef4444;"></i> 删除配置
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

    </table-page>

    <!-- 查看菜单详情弹窗 -->
    <el-dialog
      v-if="selectedRow"
      :title="`菜单配置 (游戏ID: ${selectedRow.appId}, 渠道ID: ${selectedRow.channelId})`"
      :visible.sync="detailVisible"
      width="500px"
      center
      class="premium-dialog menu-detail-dialog"
    >
      <el-descriptions border :column="1" class="menu-descriptions">
        <el-descriptions-item v-for="(menu, index) in currentMenus" :key="index" :label="menu.menuName">
          <div class="menu-url-container">
            <span class="menu-url">{{ menu.jumpUrl }}</span>
            <el-button type="success" size="mini" circle plain icon="el-icon-document-copy" class="btn-copy" @click="handleCopy(menu.jumpUrl)"></el-button>
          </div>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer" class="dialog-footer" style="text-align: right; padding-top: 10px;">
        <el-button size="small" type="primary" @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 编辑/新增菜单弹窗 -->
    <form-drawer :visible.sync="editVisible" :title="editIndex < 0 ? '新增卸载菜单' : '编辑卸载菜单'" :icon="editIndex < 0 ? 'el-icon-plus' : 'el-icon-edit-outline'" :loading="saveLoading" width="720px" @close="handleCloseEdit" @save="handleSave">
      <el-form :model="form" :rules="rules" ref="form" label-position="right" label-width="150px" size="small" class="drawer-horizontal-form">
        <el-form-item label="游戏 ID" prop="appId">
          <el-input v-model="form.appId" placeholder="请输入游戏 ID" disabled></el-input>
        </el-form-item>
        <el-form-item label="渠道 ID" prop="channelId">
          <el-input v-model="form.channelId" placeholder="请输入渠道 ID" :disabled="editIndex >= 0"></el-input>
        </el-form-item>

        <el-form-item label="菜单配置项" prop="menuArgs">
          <el-table :data="menuList" size="small" class="embedded-edit-table" style="margin-bottom: 12px;">
            <el-table-column label="菜单名称" min-width="150">
              <template slot-scope="scope">
                <el-input v-model="scope.row.menuName" size="small" placeholder="菜单名称"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="跳转链接" min-width="300">
              <template slot-scope="scope">
                <el-input v-model="scope.row.jumpUrl" size="small" placeholder="跳转链接 (http://或https://)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="70" align="center">
              <template slot-scope="scope">
                <el-button type="danger" icon="el-icon-delete" size="mini" circle plain @click="removeMenu(scope.$index)"></el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button type="primary" size="small" icon="el-icon-plus" plain @click="addMenu" v-if="menuList.length < 4" class="btn-outline-action">添加菜单项</el-button>
        </el-form-item>
      </el-form>
    </form-drawer>
  </section>
</template>

<script>
import api from '@/api'
import f from '@/filter'
import TableToolbar from '@/components/TableToolbar/index.vue'
import TablePage from '@/components/TablePage/index.vue'
import FormDrawer from '@/components/FormDrawer/index.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'uninstallMenus',
  components: { TableToolbar, TablePage, FormDrawer },
  created () {
    this.fetchData()
  },
  data () {
    return {
      tableData: [],
      tableLoading: true,
      search: '',
      visibleColumns: [],
      currentPage: 1,
      pagesize: 20,
      totalCount: 0,

      detailVisible: false,
      selectedRow: null,
      currentMenus: [],

      editVisible: false,
      editIndex: -1,
      form: {
        appId: '',
        channelId: '',
        menuArgs: '[]'
      },
      menuList: [],
      rules: {
        channelId: [{ required: true, message: '请输入渠道 ID', trigger: 'blur' }]
      },
      needReload: false,
      saveLoading: false
    }
  },
  computed: {
    appId () {
      return this.$store.getters.appId
    },
    tables () {
      const search = this.search.toLowerCase()
      if (search) {
        return this.tableData.filter(data => {
          return String(data.channelId || '').toLowerCase().indexOf(search) > -1
        })
      }
      return this.tableData
    },
    columnOptions () {
      return [
        { prop: 'createdAt', label: '创建时间' },
        { prop: 'updatedAt', label: '修改时间' }
      ]
    }
  },
  watch: {
    appId (val, oldVal) {
      if (val === oldVal) {
        return
      }
      this.fetchData()
    }
  },
  methods: {
    fetchData () {
      this.tableLoading = true
      const url = `/channel/admin/api/uninstallMenus?page=${this.currentPage - 1}&size=${this.pagesize}&sort=id,desc`
      api.get(url).then(res => {
        const embedded = res._embedded || {}
        this.tableData = embedded.uninstallMenus || embedded.uninstallMenuses || []
        this.totalCount = res.page ? res.page.totalElements : this.tableData.length
        this.tableLoading = false
      }).catch(() => {
        this.tableLoading = false
        this.$message.error('获取列表数据失败')
      })
    },
    handleReset () {
      this.fetchData()
    },
    handleSizeChange (val) {
      this.pagesize = val
      this.currentPage = 1
      this.fetchData()
    },
    handleCurrentChange (val) {
      this.currentPage = val
      this.fetchData()
    },
    getRowId (row) {
      if (row.id) return row.id
      if (row._links && row._links.self) {
        return row._links.self.href.split('/').pop()
      }
      return ''
    },
    formatTime (time) {
      return f.formatTime(time)
    },
    parseMenuArgs (menuArgsStr) {
      try {
        return JSON.parse(menuArgsStr || '[]')
      } catch (e) {
        console.error('解析菜单参数失败', e)
        return []
      }
    },
    showMenuDetail (row) {
      this.selectedRow = row
      this.currentMenus = this.parseMenuArgs(row.menuArgs)
      this.detailVisible = true
    },
    handleCopy (url) {
      navigator.clipboard.writeText(url).then(() => {
        this.$message.success('链接复制成功')
      })
    },
    handleAdd () {
      this.editIndex = -1
      this.menuList = []
      this.addMenu()
      this.form = {
        appId: this.appId || '',
        channelId: '',
        menuArgs: '[]'
      }
      this.editVisible = true
    },
    handleEdit (row, index) {
      this.editIndex = index
      this.form = JSON.parse(JSON.stringify(row))
      this.form.id = this.getRowId(row)
      this.menuList = this.parseMenuArgs(row.menuArgs)
      if (this.menuList.length === 0) {
        this.addMenu()
      }
      this.editVisible = true
    },
    handleCloseEdit () {
      this.editVisible = false
      if (this.$refs.form) this.$refs.form.clearValidate()
    },
    addMenu () {
      if (this.menuList.length < 4) {
        this.menuList.push({ menuName: '', jumpUrl: '' })
      }
    },
    removeMenu (index) {
      this.menuList.splice(index, 1)
    },
    handleSave () {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.saveLoading = true
          const form = JSON.parse(JSON.stringify(this.form))
          const menus = this.menuList.filter(item => item.menuName || item.jumpUrl)
          form.menuArgs = JSON.stringify(menus)
          form.appId = this.appId || form.appId

          const isCreate = this.editIndex < 0
          const url = isCreate
            ? '/channel/admin/api/uninstallMenus'
            : `/channel/admin/api/uninstallMenus/${form.id}`

          const promise = isCreate ? api.post(url, form) : api.put(url, form)

          promise.then(() => {
            this.$message.success(isCreate ? '添加成功' : '修改成功')
            this.editVisible = false
            this.needReload = true
            this.fetchData()
          }).catch(() => {
            this.$message.error('保存失败')
          }).finally(() => {
            this.saveLoading = false
          })
        }
      })
    },
    handleDelete (row, index) {
      this.$confirm('确定删除该卸载菜单配置吗?', '提示', { type: 'warning' }).then(() => {
        const id = this.getRowId(row)
        const url = `/channel/admin/api/uninstallMenus/${id}`
        api.delete(url).then(() => {
          this.$message.success('删除成功')
          this.needReload = true
          this.fetchData()
        }).catch(() => {
          this.$message.error('删除失败')
        })
      })
    },
    handleReload () {
      this.$confirm('确定要立即重载卸载菜单吗？该操作会调用后端重载接口。', '重载确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        api.get('/channel/admin/reload/uninstallMenus').then(res => {
          if (res.code === 200) {
            this.$message.success('重载卸载菜单成功')
            this.needReload = false
          } else {
            this.$message.error(`重载卸载菜单失败: ${res.msg}`)
          }
        }).catch(() => {
          this.$message.error('重载卸载菜单失败')
        })
      }).catch(() => {
        this.$message.info('已取消重载')
      })
    },
    handleActionCommand (command, row, index) {
      if (command === 'edit') {
        this.handleEdit(row, index)
      } else if (command === 'delete') {
        this.handleDelete(row, index)
      }
    },
    handleExport () {
      const rows = this.tables.map(row => ({
        ID: this.getRowId(row),
        游戏ID: row.appId,
        渠道ID: row.channelId,
        卸载菜单: row.menuArgs,
        创建时间: this.formatTime(row.createdAt),
        修改时间: this.formatTime(row.updatedAt)
      }))
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '卸载菜单')
      XLSX.writeFile(workbook, `卸载菜单-${this.$moment().format('YYYYMMDDHHmmss')}.xlsx`)
    }
  }
}
</script>

<style scoped>
.menu-workspace {
  padding: 16px 24px;
}

/* 过滤筛选卡片 */
.filter-card {
  margin-top: 0 !important;
  margin-bottom: 20px;
  padding: 16px 24px;
  border-radius: var(--border-radius-card);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-base);
}

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-left-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-right-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-action {
  flex-shrink: 0;
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.btn-action:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.btn-reset {
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.btn-reset:hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
  border-color: var(--border-hover) !important;
}

.btn-reload {
  background-color: var(--bg-reload) !important;
  border-color: var(--border-reload) !important;
  color: var(--text-reload) !important;
  height: 32px;
  padding: 0 16px;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.btn-reload:hover,
.btn-reload:focus {
  background-color: var(--bg-reload-hover) !important;
  border-color: var(--border-reload-hover) !important;
  color: var(--text-reload-hover) !important;
}

.reload-tip {
  color: #EF4444;
  font-size: 13px;
  font-weight: 500;
}

.btn-setting {
  flex-shrink: 0;
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  border-radius: 8px !important;
  height: 32px;
  padding: 0 16px;
}

.btn-setting:hover {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-hover) !important;
  color: var(--text-main) !important;
}

.search-input {
  width: 220px;
  flex-shrink: 0;
}

.search-input :deep(.el-input__inner) {
  border-radius: 8px;
  border-color: var(--border-color);
  height: 32px;
  line-height: 32px;
  font-weight: 500;
  background-color: var(--bg-card);
  color: var(--text-main);
  transition: all 0.2s ease;
}

.search-input :deep(.el-input__inner:hover) {
  border-color: var(--border-hover);
}

.search-input :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

/* 表格卡片样式 */
.table-card {
  padding: 20px;
  border-radius: var(--border-radius-card);
  background-color: var(--bg-card);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-base);
}

.premium-table {
  border-radius: 8px;
  overflow: hidden;
  border: none !important;
  color: var(--text-main) !important;
}

.premium-table::before {
  display: none !important;
}

.premium-table :deep(.el-table__header-wrapper) th,
.premium-table :deep(.el-table__fixed-header-wrapper) th,
.premium-table :deep(.el-table__fixed-right .el-table__fixed-header-wrapper) th {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  font-weight: 700 !important;
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.premium-table :deep(.el-table__row) td {
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-light) !important;
}

.premium-table :deep(.el-table__row) {
  transition: background-color 0.2s ease;
}

.premium-table :deep(.el-table__row:hover) td,
.premium-table :deep(.el-table__body tr.hover-row > td),
.premium-table :deep(.el-table__body tr.hover-row.current-row > td),
.premium-table :deep(.el-table__body tr.hover-row.el-table__row--striped > td) {
  background-color: var(--bg-hover) !important;
}

.cell-id,
.cell-text-gray {
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace !important;
  font-size: 12.5px !important;
}

.cell-id {
  font-weight: 600;
  color: var(--text-muted);
}

.cell-text-gray {
  color: var(--text-muted);
}

.cell-name-link {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  text-decoration: none !important;
}

.menu-detail-link {
  font-weight: 600;
  font-size: 13.5px;
  text-decoration: none !important;
}

.menu-detail-link:hover {
  text-decoration: none !important;
}

.time-text {
  color: var(--text-muted);
  font-size: 13px;
}

/* 操作列三点图标 */
.action-btn {
  font-size: 12px;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  background: var(--bg-card);
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--bg-hover);
  color: var(--text-main);
  border-color: var(--border-hover);
}

/* 分页器 */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.premium-pagination :deep(.btn-prev),
.premium-pagination :deep(.btn-next),
.premium-pagination :deep(.el-pager li) {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  margin: 0 3px !important;
}

.premium-pagination :deep(.el-pager li.active) {
  background-color: var(--primary-color) !important;
  color: #FFFFFF !important;
}

.premium-pagination :deep(.el-pagination__sizes .el-input__inner),
.premium-pagination :deep(.el-pagination__editor.el-input .el-input__inner) {
  border-radius: 6px;
  border-color: var(--border-color);
  background-color: var(--bg-card);
  color: var(--text-main);
}

/* 列配置 Popover 内部样式 */
.popover-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text-main);
  padding: 6px 4px;
}

.popover-divider {
  height: 1px;
  background: var(--border-color);
  margin: 6px 0;
}

.popover-body-scroll {
  max-height: 250px;
  overflow-y: auto;
  padding: 4px;
}

.popover-body-scroll::-webkit-scrollbar {
  width: 4px;
}

.popover-body-scroll::-webkit-scrollbar-thumb {
  background: var(--border-hover);
  border-radius: 2px;
}

.popover-body-scroll :deep(.el-checkbox) {
  display: block;
  margin-bottom: 8px;
  margin-right: 0;
}

/* 弹窗及查看面板 */
.premium-dialog :deep(.el-dialog) {
  border-radius: 12px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  overflow: hidden;
  background-color: var(--bg-card) !important;
}

.menu-descriptions :deep(.el-descriptions-item__label) {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  border-color: var(--border-color) !important;
  font-weight: 600;
  width: 140px;
}

.menu-descriptions :deep(.el-descriptions-item__content) {
  border-color: var(--border-color) !important;
  background-color: var(--bg-card) !important;
  color: var(--text-main) !important;
}

.menu-url-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.menu-url {
  word-break: break-all;
  color: var(--text-main);
  font-size: 13px;
}

.btn-copy {
  flex-shrink: 0;
  margin-left: 8px;
}

.embedded-edit-table {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--bg-card);
}

.embedded-edit-table :deep(.el-table__header-wrapper) th {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  border-bottom: 1px solid var(--border-color) !important;
  font-weight: 600 !important;
}

.embedded-edit-table :deep(.el-table__row) td {
  border-bottom: 1px solid var(--border-light) !important;
}

.btn-outline-action {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
}

.btn-outline-action:hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
  border-color: var(--border-hover) !important;
}

.btn-save {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
}

.btn-save:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}
</style>
